import React from "react"
import { ScadenzaDto, TipoScadenza } from "../../generated"
import { DefaultCommaSeparatedExportConfiguration, GetTable } from "../../common/components/DataTable";
import { Link } from "../../common/components/link";
import { links } from "../../routes";
import DatesHelper from "../../common/datesHelper";
import { EditInterventoButton } from "../../interventi/components/InterventiHelpers";
import { PhoneComponents } from "../../common/components/PhoneComponent";

const Table = GetTable<ScadenzaDto>();

const getExpirationStatusString = (type: TipoScadenza) => {
    const status: string[] = [];
    if (type & TipoScadenza.ControlloAnnuale)
        status.push("Controllo annuale scaduto")
    if (type & TipoScadenza.ControlloCombustione)
        status.push("Controllo combustione scaduto")
    if (type & TipoScadenza.ContrattoScaduto)
        status.push("Contratto manutenzione scaduto")
    return status.join(", ");
}

const getExpirationStatusIcon = (type: TipoScadenza) => {
    const icons: JSX.Element[] = [];
    if (type & TipoScadenza.ControlloAnnuale)
        icons.push(<i
            key={TipoScadenza.ControlloAnnuale}
            className="far fa-calendar-alt fa-lg me-1 text-danger"
            title={getExpirationStatusString(type)}
        />);
    if (type & TipoScadenza.ControlloCombustione)
        icons.push(<i
            key={TipoScadenza.ControlloCombustione}
            className="fas fa-burn fa-lg me-1 text-danger"
            title={getExpirationStatusString(type)}
        />);
    if (type & TipoScadenza.ContrattoScaduto)
        icons.push(<i
            key={TipoScadenza.ContrattoScaduto}
            className="fas fa-file-contract fa-lg me-1 text-danger"
            title={getExpirationStatusString(type)}
        />);
    return icons;
}

export const ExpirationLegend = () => {
    return (
        <>
            <div className="me-3">
                <span className="me-2">{getExpirationStatusIcon(TipoScadenza.ControlloAnnuale)}</span>{getExpirationStatusString(TipoScadenza.ControlloAnnuale)}
            </div>
            <div className="me-3">
                <span className="me-2">{getExpirationStatusIcon(TipoScadenza.ControlloCombustione)}</span>{getExpirationStatusString(TipoScadenza.ControlloCombustione)}
            </div>
            <div className="me-3">
                <span className="me-2">{getExpirationStatusIcon(TipoScadenza.ContrattoScaduto)}</span>{getExpirationStatusString(TipoScadenza.ContrattoScaduto)}
            </div>
        </>
    )
}

export const ExpirationsTable: React.FunctionComponent<{
    data: ScadenzaDto[], selectedItems: ScadenzaDto[],
    onSelectRow: (expirations: ScadenzaDto) => void,
    onSelect: (expirations: ScadenzaDto[]) => void
}> = ({ data, selectedItems, onSelectRow, onSelect }) => {
    return (
        <Table
            data={data}
            keySelector={x => x.idImpianto.toString()}
            storagekey="cmexpirationtable"
            onRowClick={onSelectRow}
            exportConfiguration={DefaultCommaSeparatedExportConfiguration}
            filterables={[x => x.nome,
            x => x.cognome,
            x => x.responsabile,
            x => x.marca,
            x => x.modello,
            x => x.tel,
            x => x.cell,
            x => x.indirizzo,
            x => x.comune,
            x => x.frazione,
            x => x.note]}
        >
            <Table.SelectColumn
                selectedItems={selectedItems}
                onSelectionChanged={onSelect}
                noPrint
            />
            <Table.Column
                header="Stato"
                template={x => getExpirationStatusIcon(x.tipologiaScadenza)}
                groupBy={x => getExpirationStatusString(x.tipologiaScadenza)}
                exportable={x => getExpirationStatusString(x.tipologiaScadenza)}
            />
            <Table.Column
                header="Nominativo"
                template={x => {
                    const proprietario = `${x.cognome} ${x.nome}`;
                    const inquilino = x.responsabile;
                    return <Link
                        to={links.customerShow(x.idCliente)}>
                        <div>{proprietario}</div>
                        {inquilino && proprietario != inquilino && <div>{inquilino}</div>}
                    </Link>
                }}
                orderBy={x => `${x.cognome} ${x.nome}`}
                groupable={false}
                exportable={x => `${x.cognome} ${x.nome}`}
            />
            <Table.Column
                header="Caldaia"
                template={x => <>
                    <div style={{ whiteSpace: "nowrap" }}>
                        {`${x.marca} - ${x.modello}`}
                    </div>
                    <div>{`${x.potenzaFocolare && x.potenzaFocolare > 0 ? x.potenzaFocolare : "N.C."} KW`}</div>
                </>}
                orderBy={x => `${x.marca} - ${x.modello}`}
                groupBy={x => `${x.marca} - ${x.modello}`}
                exportable={x => `${x.marca} - ${x.modello}`}
            />
            <Table.Column
                header="Località"
                template={x => x.frazione != x.comune ? x.frazione : x.comune}
                exportable={x => x.frazione != x.comune ? x.frazione ?? "" : x.comune ?? ""}
            />
            <Table.Column
                header="Prov"
                template={x => x.provincia}
                exportable={x => x.provincia ?? ""}
            />
            <Table.Column
                header="Indirizzo"
                template={x => x.indirizzo}
                exportable={x => x.indirizzo ?? ""}
            />
            <Table.Column
                header="Telefono"
                template={x =>
                    <>
                        {x.cell && <PhoneComponents tel={x.cell}
                            showWhatsApp
                        // whatsAppText={
                        //     `Gent. le CLIENTE, Le ricordiamo che in data ${DatesHelper.getReadableDateString(x.da)} alle ore 08:00 un nostro tecnico verrà ad effettuare un intervento di Manutenzione alla caldaia al vostro impianto termico matr. Cordiali saluti, IDRALTECNO DI ANDREA SPENA`
                        // }
                        />}
                        {x.tel && <PhoneComponents tel={x.tel} />}
                    </>}
                orderable={false}
                groupable={false}
                exportable={x => `${x.cell ?? ""} ${x.tel ?? ""}`}
            />
            <Table.Column
                header="Locale"
                className="text-center"
                headerClassName="text-center"
                template={x => x.localeInstallazione}
                exportable={x => x.localeInstallazione ?? ""}
                hidden
            />
            <Table.Column
                header="Ult.Contr.Ann"
                template={x => DatesHelper.getReadableDateString(x.dataUltimoControlloAnn)}
                orderBy={x => x.dataUltimoControlloAnn}
                exportable={x => DatesHelper.getReadableDateString(x.dataUltimoControlloAnn)}
            />
            <Table.Column
                header="Ult.Contr.Comb"
                template={x => DatesHelper.getReadableDateString(x.dataUltimoControlloComb)}
                orderBy={x => x.dataUltimoControlloComb}
                exportable={x => DatesHelper.getReadableDateString(x.dataUltimoControlloComb)}
            />
            <Table.Column
                header="Scad.Contratto"
                className="text-center"
                headerClassName="text-center"
                template={x => x.contractExpiration && DatesHelper.getReadableCompactDateString(x.contractExpiration) || "-"}
                orderable={false}
                groupable={false}
                exportable={x => x.contractExpiration && DatesHelper.getReadableCompactDateString(x.contractExpiration) || "-"}
            />
            <Table.Column
                header="Scad.Bollino"
                className="text-center"
                headerClassName="text-center"
                template={x => x.lastBollino && DatesHelper.getReadableCompactDateString(x.lastBollino.validity.to) || "-"}
                orderBy={x => x.lastBollino?.validity.to?.valueOf() ?? Number.MAX_VALUE}
                groupable={false}
                exportable={x => x.lastBollino && DatesHelper.getReadableCompactDateString(x.lastBollino.validity.to) || "-"}
            />
            <Table.Column
                header="Pianificato"
                groupBy={x => x.idIntervento != null ? "Pianificato" : "Da pianificare"}
                orderBy={x => x.idIntervento != null ? "Pianificato" : "Da pianificare"}
                template={x =>
                    <EditInterventoButton
                        title="Pianifica intervento"
                        className={`btn ${x.idIntervento ? "btn-success" : "btn-outline-secondary"} btn-sm d-flex align-items-center text-center`}
                        idIntervento={x.idIntervento}
                        idCliente={x.idCliente}
                        idImpianto={x.idImpianto}
                        style={{ width: 90 }}
                    >
                        {x.scheduledDateTime
                            && <div className="text-uppercase" style={{ whiteSpace: "nowrap" }}>
                                <div style={{ fontSize: "0.6rem" }} className="mb-2">Pianificato il</div>
                                <div style={{ fontSize: "0.7rem" }}>
                                    {DatesHelper.getReadableDateTimeString(x.scheduledDateTime!)}
                                </div>
                            </div>
                            || <div><i className="fas fa-plus me-2" /> <span style={{ fontSize: "0.8rem" }}>Pianifica intervento</span></div>}
                    </EditInterventoButton>}
                exportable={x => x.scheduledDateTime ? DatesHelper.getReadableDateTimeString(x.scheduledDateTime) : "Non pianificato"}
            />

            <Table.Column
                header="Note"
                template={x => <div style={{ fontSize: "0.8rem" }}>
                    {x.note}
                </div>}
                exportable={x => x.note ?? ""}
            />
        </Table >
    )
}